/* eslint-disable func-names */
import {
  Flexbox,
  Form, LoadManager, PageHeader, Styles,
} from '@/components'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import { ANIMAL } from '@/lib/enums'
import yup from '@/lib/yupPt'
import categoriesApi from '@/services/api/categories'
import params from '@/services/api/params'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

yup.addMethod(yup.number, 'cancelNonScheduledCastrationAfterDays', function () {
  return this.test('test-cancel-non-scheduled-castration-after-days', 'Deve ser no mínimo 20', function (value) {
    const { createError, path } = this
    if (value && value !== 0 && value < 20) {
      return createError({ path, message: 'Deve ser no mínimo 20' })
    }
    return true
  })
})

const schema = () => yup.object().shape({
  maxCastrationsPerCommonAccount: yup.number().min(0).required(),
  maxCastrationsPeriodDays: yup.number().min(0).required(),
  maxClinicInboxCastrations: yup.number().min(0).required(),
  cancelNonScheduledCastrationAfterDays: yup
    .number()
    .cancelNonScheduledCastrationAfterDays()
    .required(),
  cancelNonScheduledCastrationAfterDaysMessage: yup.string().when('cancelNonScheduledCastrationAfterDays', {
    is: (v) => v >= 20,
    then: yup.string().min(10).required(),
  }),
  maxAgeToRequestCastrations: yup.mixed(),
})

function Params({ title }) {
  usePageTitle({ title })
  const [updating, setUpdating] = useState(false)
  const { loading, data, error } = useAxiosRequest(params.list)
  const { data: categories, loading: loadingCategories } = useAxiosRequest(categoriesApi.list)

  async function onSubmit(body) {
    try {
      setUpdating(true)
      await params.update(body)
      toast.success('Parâmetros atualizados')
    } catch (e) {
      toast.error(e?.message)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Parâmetros"
        icon="fas fa-cogs"
        subtitle="Parâmetros do sistema"
      />
      <LoadManager loading={loading || loadingCategories} error={error}>
        <Flexbox.Columns>
          <Flexbox.Column className="col-6 col-sm-12">
            {
              Boolean(data && data.maxAgeToRequestCastrations) && (
                <fieldset disabled={updating}>
                  <Form.Formik
                    initialValues={data}
                    onSubmit={onSubmit}
                    validationSchema={schema}
                    validateOnMount
                  >
                    {
                      ({
                        errors, touched, isValid, values,
                      }) => (

                        <Form.ValidationForm>
                          <div className="columns">
                            <Form.Group className={`column ${touched?.maxCastrationsPerCommonAccount && errors?.maxCastrationsPerCommonAccount ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="maxCastrationsPerCommonAccount">Limite de castrações por CPF <span className="text-error">*</span></Form.Label>
                              <Form.ValidationField
                                name="maxCastrationsPerCommonAccount"
                                id="maxCastrationsPerCommonAccount"
                                type="number"
                                placeholder="Limite de castrações por CPF"
                              />
                              {
                                touched?.maxCastrationsPerCommonAccount
                                  && errors?.maxCastrationsPerCommonAccount
                                  ? <Form.Hint className="text-error">{errors.maxCastrationsPerCommonAccount}</Form.Hint> : null
                              }
                            </Form.Group>
                            <Form.Group className={`column ${touched?.maxCastrationsPeriodDays && errors?.maxCastrationsPeriodDays ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="maxCastrationsPeriodDays">Período do limite (em dias) <span className="text-error">*</span></Form.Label>
                              <Form.ValidationField
                                name="maxCastrationsPeriodDays"
                                id="maxCastrationsPeriodDays"
                                type="number"
                                placeholder="Período do limite (em dias)"
                              />
                              {
                                touched?.maxCastrationsPeriodDays
                                  && errors?.maxCastrationsPeriodDays
                                  ? <Form.Hint className="text-error">{errors.maxCastrationsPeriodDays}</Form.Hint> : null
                              }
                            </Form.Group>
                          </div>
                          <Form.Hint className="text-gray mb-2">
                            Este parâmetro define a quantidade máxima de castrações por
                            usuário comum, dentro do período estabelecido.
                            ONGs e protetores não são afetados.
                            <br />Castrações rejeitadas e canceladas não são consideradas
                            <br /><b>Para desabilitar deixe os limites com o valor 0</b>
                          </Form.Hint>
                          <Form.Group className={`${touched?.maxClinicInboxCastrations && errors?.maxClinicInboxCastrations ? 'has-error' : ''}`}>
                            <Form.Label htmlFor="maxClinicInboxCastrations">Máximo de castrações na caixa da clínica <span className="text-error">*</span></Form.Label>
                            <Form.ValidationField
                              name="maxClinicInboxCastrations"
                              id="maxClinicInboxCastrations"
                              type="number"
                              placeholder="Máximo de castrações na caixa da clínica"
                            />
                            {
                              touched?.maxClinicInboxCastrations
                                && errors?.maxClinicInboxCastrations
                                ? <Form.Hint className="text-error">{errors.maxClinicInboxCastrations}</Form.Hint> : null
                            }
                            <Form.Hint>
                              Este parâmetro define o máximo de castrações que podem ficar na caixa
                              das clínicas, caso todas as clínicas estejam com a caixa cheia as
                              castrações ficarão pré aprovadas enquanto não houver
                              clínica disponível
                            </Form.Hint>
                          </Form.Group>

                          <div className="divider" />
                          <Form.Group className={`${touched?.cancelNonScheduledCastrationAfterDays && errors?.cancelNonScheduledCastrationAfterDays ? 'has-error' : ''}`}>
                            <Form.Label htmlFor="cancelNonScheduledCastrationAfterDays">Cancelar castrações aprovadas não agendadas após quantos dias <span className="text-error">*</span></Form.Label>
                            <Form.ValidationField
                              name="cancelNonScheduledCastrationAfterDays"
                              id="cancelNonScheduledCastrationAfterDays"
                              type="number"
                              min="0"
                              placeholder="Castrações por CPF"
                            />
                            {
                              touched?.cancelNonScheduledCastrationAfterDays
                                && errors?.cancelNonScheduledCastrationAfterDays
                                ? <Form.Hint className="text-error">{errors.cancelNonScheduledCastrationAfterDays}</Form.Hint> : null
                            }
                            <Form.Hint className="text-gray">
                              Se definido, as castrações que não forem agendadas após a quantidade
                              de dias informada, serão canceladas automaticamente
                              <br />Os usuários receberão um aviso pelo aplicativo antes
                              da castração ser cancelada
                              <br /><b>Para desabilitar deixe o parâmetro com o valor 0</b>
                            </Form.Hint>
                          </Form.Group>

                          {
                            values.cancelNonScheduledCastrationAfterDays >= 20 && (
                              <Form.Group className={`${touched?.cancelNonScheduledCastrationAfterDaysMessage && errors?.cancelNonScheduledCastrationAfterDaysMessage ? 'has-error' : ''}`}>
                                <Form.Label htmlFor="cancelNonScheduledCastrationAfterDaysMessage">Mensagem automática de cancelamento da castração <span className="text-error">*</span></Form.Label>
                                <Form.ValidationField
                                  name="cancelNonScheduledCastrationAfterDaysMessage"
                                  component="textarea"
                                  rows="4"
                                  id="cancelNonScheduledCastrationAfterDaysMessage"
                                  placeholder="Por determinação da lei ##/#### a castração foi cancelada pois não houve agendamento ..."
                                />
                                {
                                  touched?.cancelNonScheduledCastrationAfterDaysMessage
                                    && errors?.cancelNonScheduledCastrationAfterDaysMessage
                                    ? <Form.Hint className="text-error">{errors.cancelNonScheduledCastrationAfterDaysMessage}</Form.Hint>
                                    : null
                                }

                                <Form.Hint className="text-gray">
                                  Isto será adicionado ao cancelamento automático da castração
                                </Form.Hint>
                              </Form.Group>
                            )
                          }

                          <div className="divider" />
                          <Form.Group className={`${touched?.maxAgeToRequestCastrations && errors?.maxAgeToRequestCastrations ? 'has-error' : ''}`}>
                            <Form.Label>
                              Idade máxima para solicitar castrações (em anos)
                            </Form.Label>
                            {
                              categories.map((category) => (
                                <div className="mb-2 bg-gray s-rounded p-2" key={category.id}>
                                  <div>{category.description}</div>
                                  <div className="columns">
                                    {
                                      Object.values(ANIMAL.gender.enum).map((gender) => (
                                        <div key={gender} className="input-group column">
                                          <span className="input-group-addon addon-sm">{ANIMAL.gender.t(gender)}</span>
                                          <Form.ValidationField
                                            name={`maxAgeToRequestCastrations.${category.id}.${gender}`}
                                            type="number"
                                            step={1}
                                            min={0}
                                            className="form-input input-sm"
                                            placeholder="Anos"
                                          />
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              ))
                            }
                          </Form.Group>
                          <Form.Hint className="text-gray">
                            Se definido, os usuários não poderão solicitar castrações
                            para animais com idade superior a este valor.
                            <br /><b>Para desabilitar deixe o parâmetro com o valor 0</b>
                          </Form.Hint>
                          <div className="divider" />
                          <div className="columns">
                            <Form.Group className={`column ${touched?.maxClinicalCaresPerCommonAccount && errors?.maxClinicalCaresPerCommonAccount ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="maxClinicalCaresPerCommonAccount">Limite de atendimentos por CPF <span className="text-error">*</span></Form.Label>
                              <Form.ValidationField
                                name="maxClinicalCaresPerCommonAccount"
                                id="maxClinicalCaresPerCommonAccount"
                                type="number"
                                placeholder="Limite de atendimentos por CPF"
                              />
                              {
                                touched?.maxClinicalCaresPerCommonAccount
                                  && errors?.maxClinicalCaresPerCommonAccount
                                  ? <Form.Hint className="text-error">{errors.maxClinicalCaresPerCommonAccount}</Form.Hint> : null
                              }
                            </Form.Group>
                          </div>
                          <div className="divider" />

                          <div className="text-right">
                            <button type="submit" disabled={!isValid} className={`btn btn-primary ${updating ? 'loading' : ''}`}>
                              <i className="fas fa-save" />
                              &nbsp;Salvar
                            </button>
                          </div>
                        </Form.ValidationForm>
                      )
                    }
                  </Form.Formik>
                </fieldset>
              )
            }
          </Flexbox.Column>
        </Flexbox.Columns>
      </LoadManager>
    </Styles.Container>
  )
}

export default Params
